import {
  Clipboard,
  Copy,
  ExternalLink,
  Eye,
  FilePlus2,
  FolderPlus,
  Info,
  Link2,
  Monitor,
  Palette,
  RefreshCw,
  RotateCcw,
  Scissors,
  SortAsc,
  SquareTerminal,
  Trash2,
} from "lucide-react";
import { separator } from "./components/ContextMenu";
import {
  createFolder,
  createTextFile,
  deleteNode,
  emptyRecycleBin,
  pathOf,
  restoreNode,
} from "./fs";

/**
 * Shared context-menu definitions, so the desktop and File Explorer offer the
 * same commands for the same things.
 */

export const VIEW_SIZES = [
  { id: "large", label: "Large icons" },
  { id: "medium", label: "Medium icons" },
  { id: "small", label: "Small icons" },
  { id: "details", label: "Details" },
];

export const SORT_KEYS = [
  { id: "name", label: "Name" },
  { id: "type", label: "Item type" },
  { id: "size", label: "Size" },
  { id: "modified", label: "Date modified" },
];

const copyToClipboard = (text) => {
  navigator.clipboard?.writeText(text).catch(() => {});
};

/** Menu for a file or folder, wherever it is shown. */
export function itemMenu({ node, open, openProperties, apps = [] }) {
  const inBin = Boolean(node.deletedFrom);

  if (inBin) {
    return [
      { label: "Restore", icon: RotateCcw, onClick: () => restoreNode(node.id) },
      separator,
      {
        label: "Delete permanently",
        icon: Trash2,
        danger: true,
        onClick: () => {
          /* Emptying one item is the same operation on a one-item bin. */
          deleteNode(node.id);
        },
        disabled: true,
      },
      separator,
      {
        label: "Properties",
        icon: Info,
        onClick: () => openProperties(node.id),
      },
    ];
  }

  const isFolder = node.type !== "file";
  const opener = apps.find((a) => a.id === node.open?.app);

  return [
    {
      type: "icons",
      items: [
        { icon: Scissors, label: "Cut", disabled: true },
        {
          icon: Copy,
          label: "Copy path",
          onClick: () => copyToClipboard(pathOf(node.id)),
        },
        { icon: Clipboard, label: "Paste", disabled: true },
        {
          icon: Trash2,
          label: "Delete",
          onClick: () => deleteNode(node.id),
        },
      ],
    },
    {
      label: "Open",
      icon: isFolder ? Eye : ExternalLink,
      onClick: () =>
        isFolder
          ? open("explorer", { folderId: node.target || node.id })
          : node.open?.app && open(node.open.app, { fileId: node.id, ...node.open }),
    },
    ...(opener
      ? [
          {
            label: "Open with",
            icon: opener.icon,
            items: [
              {
                label: opener.title,
                icon: opener.icon,
                onClick: () =>
                  open(node.open.app, { fileId: node.id, ...node.open }),
              },
              {
                label: "Notepad",
                icon: Copy,
                disabled: !node.text,
                onClick: () => open("notepad", { fileId: node.id }),
              },
            ],
          },
        ]
      : []),
    ...(node.open?.url
      ? [
          {
            label: "Copy link address",
            icon: Link2,
            onClick: () => copyToClipboard(node.open.url),
          },
        ]
      : []),
    {
      label: "Copy as path",
      icon: Copy,
      shortcut: "Ctrl+Shift+C",
      onClick: () => copyToClipboard(pathOf(node.id)),
    },
    separator,
    {
      label: "Delete",
      icon: Trash2,
      shortcut: "Del",
      danger: true,
      onClick: () => deleteNode(node.id),
    },
    separator,
    {
      label: "Properties",
      icon: Info,
      shortcut: "Alt+Enter",
      onClick: () => openProperties(node.id),
    },
  ];
}

/** Menu for the empty space of a folder (or the desktop). */
export function backgroundMenu({
  folderId,
  view,
  setView,
  sort,
  setSort,
  onRefresh,
  extra = [],
  tail = [],
  canCreate = true,
}) {
  return [
    {
      label: "View",
      icon: Eye,
      items: VIEW_SIZES.map((size) => ({
        label: size.label,
        checked: view === size.id,
        onClick: () => setView(size.id),
      })),
    },
    {
      label: "Sort by",
      icon: SortAsc,
      items: SORT_KEYS.map((key) => ({
        label: key.label,
        checked: sort === key.id,
        onClick: () => setSort(key.id),
      })),
    },
    {
      label: "Refresh",
      icon: RefreshCw,
      shortcut: "F5",
      onClick: onRefresh,
    },
    separator,
    {
      label: "New",
      icon: FolderPlus,
      disabled: !canCreate,
      items: [
        {
          label: "Folder",
          icon: FolderPlus,
          onClick: () => createFolder(folderId),
        },
        {
          label: "Text Document",
          icon: FilePlus2,
          onClick: () => createTextFile(folderId),
        },
      ],
    },
    ...(extra.length ? [separator, ...extra] : []),
    ...(tail.length ? [separator, ...tail] : []),
  ];
}

/** Menu for This PC — where the computer info lives. */
export function thisPcMenu({ open, openSystemProperties }) {
  return [
    {
      label: "Open",
      icon: Monitor,
      onClick: () => open("explorer", { folderId: "this-pc" }),
    },
    {
      label: "Manage",
      icon: SquareTerminal,
      onClick: () => open("taskmgr"),
    },
    separator,
    {
      label: "Personalise",
      icon: Palette,
      onClick: () => open("settings"),
    },
    separator,
    {
      label: "Properties",
      icon: Info,
      shortcut: "Alt+Enter",
      onClick: openSystemProperties,
    },
  ];
}

/** Menu for the Recycle Bin. */
export function recycleBinMenu({ open, count, openProperties }) {
  return [
    {
      label: "Open",
      icon: Trash2,
      onClick: () => open("explorer", { folderId: "recycle-bin" }),
    },
    {
      label: "Empty Recycle Bin",
      icon: Trash2,
      danger: true,
      disabled: !count,
      onClick: emptyRecycleBin,
    },
    separator,
    {
      label: "Properties",
      icon: Info,
      onClick: () => openProperties("recycle-bin"),
    },
  ];
}
